import type { ReactNode } from "react";
import { cn } from "@/components/ui/cn";

type VDataTableColumn<T> = {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
  align?: "left" | "center" | "right";
};

type VDataTableProps<T> = {
  columns: ReadonlyArray<VDataTableColumn<T>>;
  rows: ReadonlyArray<T>;
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyState?: ReactNode;
  className?: string;
  /** "top" lijnt alle cellen bovenaan uit — voor tabellen met meerregelige cellen. */
  rowAlign?: "middle" | "top";
};

const alignClass = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

export function VDataTable<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  emptyState,
  className,
  rowAlign = "middle",
}: VDataTableProps<T>): React.JSX.Element {
  if (rows.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }
  return (
    <div className={cn("overflow-x-auto rounded-xl border border-border bg-card shadow-xs", className)}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn(
                  "px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground first:pl-5 last:pr-5",
                  alignClass[col.align ?? "left"],
                  col.headerClassName,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => (
            <tr
              key={getRowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                "transition-colors",
                onRowClick && "cursor-pointer hover:bg-muted/40",
              )}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={cn(
                    "px-4 py-3 text-foreground first:pl-5 last:pr-5",
                    rowAlign === "top" && "align-top",
                    alignClass[col.align ?? "left"],
                    col.className,
                  )}
                >
                  {col.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export type { VDataTableColumn, VDataTableProps };
